/**
 * Color comparator: compare list (localStorage, max 5), comparator buttons, search bar
 * and cards. Handles add/remove from list, render cards into [data-comparison-cards-list],
 * drag-and-drop reorder, search dropdown filter, Add/Reset. Used by compare-colours block
 * and by <comparator-button> on color cards.
 *
 * High-level structure:
 * - Storage helpers: read/write the compare list from localStorage.
 * - Card rendering: build cards into the HTML container created by
 *   `snippets/color-comparison-cards-list.liquid` (div with data-comparison-cards-list).
 * - Card interactions: drag-and-drop columns and per-card remove (X).
 * - Comparator buttons: click handling for the swatch icons on color cards.
 * - Floating compare button: count + visibility based on the current list.
 * - Search bar: dropdown filter, Add and Reset controls on the comparator page.
 */

// --- Storage & shared constants ---------------------------------------------------

const STORAGE_KEY = 'color-compare-list';
const MAX_ITEMS = 5;
const FILL_CLASS = 'fill-comparator';
const DISABLED_CLASS = 'comparator-button--disabled';
const WARNING_MESSAGE = 'You can compare up to only 5 items';

// --- Read/write helpers -----------------------------------------------------------

/**
 * Splits a string by comma and trims each part. Returns empty array if blank.
 * @param {string} value - Comma-separated string from data attribute
 * @returns {string[]}
 */
function parseCommaList(value) {
  if (value == null || String(value).trim() === '') return [];
  return String(value).split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Builds the color object from a comparator button’s data attributes.
 * Comma-separated attributes become arrays (colorcollection, colorfilter, coloratmosphere).
 * @param {HTMLButtonElement} button - The button with data-color* attributes
 * @returns {object} Color object for storage
 */
function getColorObjectFromButton(button) {
  /** @param {string} key */
  const get = (key, def = '') =>
    (button.getAttribute(key) ?? def).trim();
  return {
    url: get('data-colorurl'),
    hexcode: get('data-colorcode'),
    colorId: get('data-colorid'),
    colorName: get('data-colorname'),
    colorRef: get('data-colorref'),
    description: get('data-colordescription'),
    colorBase: get('data-colorbase'),
    colorcollection: parseCommaList(get('data-colorcollection')),
    colorfilter: parseCommaList(get('data-colorfilter')),
    coloratmosphere: parseCommaList(get('data-coloratmosphere')),
  };
}

/**
 * Reads the compare list from localStorage.
 * @returns {object[]}
 */
function getStoredList() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/**
 * Writes the compare list to localStorage and dispatches a custom event.
 * @param {object[]} list - Full compare list
 */
function setStoredList(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent('color-compare-list-updated', { detail: { list } }));
}

// --- Card rendering into the comparator block ------------------------------------

/**
 * @typedef {Object} CompareColor
 * @property {string} [url]
 * @property {string} [hexcode]
 * @property {string | number} [colorId]
 * @property {string} [colorName]
 * @property {string} [colorRef]
 * @property {string} [description]
 * @property {string} [colorBase]
 * @property {string[]} [colorcollection]
 * @property {string[]} [colorfilter]
 * @property {string[]} [coloratmosphere]
 */

/**
 * Renders compare cards from the given list into all comparison card lists.
 * The containers themselves are created in Liquid by
 * `snippets/color-comparison-cards-list.liquid`:
 *   <div data-comparison-cards-list>…</div>
 * This function replaces the contents of those containers with cards built
 * from the current localStorage list.
 * @param {CompareColor[]} list
 */
function renderCompareCardsIntoAllLists(list) {
  /** @type {NodeListOf<HTMLElement>} */
  const containers = document.querySelectorAll('[data-comparison-cards-list]');
  if (!containers.length) {
    return;
  }

  /** @type {HTMLTemplateElement | null} */
  const template = document.querySelector('#color-comparison-card-template');

  for (const container of containers) {
    renderCardsIntoContainer(container, list, template);
  }
}

/**
 * Renders all cards into a single container.
 * @param {HTMLElement} container
 * @param {CompareColor[]} list
 * @param {HTMLTemplateElement | null} template
 */
function renderCardsIntoContainer(container, list, template) {
  container.innerHTML = '';

  if (!list.length) {
    return;
  }

  for (const color of list) {
    const card = createCompareCard(color, template);
    container.appendChild(card);
  }

  attachCardInteractions(container);
}

/**
 * Creates a single compare card element from a color object.
 *
 * Card markup exists in two places by design:
 * - Primary source of truth: Liquid template in
 *   `snippets/color-comparison-cards-list.liquid` (template#color-comparison-card-template).
 *   When that template is present, we clone it so any HTML/CSS changes are done in Liquid.
 * - JS fallback below: used only when the template cannot be found (e.g. if the block
 *   is removed or not rendered on a page). This prevents the feature from failing
 *   completely and provides a minimal layout for debugging.
 *
 * When updating card structure or classes, prefer changing the Liquid template and keep
 * this fallback roughly in sync.
 * @param {CompareColor} color
 * @param {HTMLTemplateElement | null} template
 * @returns {HTMLElement}
 */
function createCompareCard(color, template) {
  /** @type {HTMLElement | null} */
  let card = null;

  if (template && template.content && template.content.firstElementChild) {
    card = /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true));
  } else {
    card = document.createElement('div');
    card.className = 'color-compare-card';
    card.innerHTML = `
      <a href="" class="color-compare-card__link" aria-label="View color"></a>
      <div class="color-compare-card__swatch">
        <div class="color-compare-card__swatch-color"></div>
      </div>
      <div class="color-comparison-search-cards__card-content">
        <div class="color-compare-card__top">
          <div class="color-compare-card__title">
            <div class="color-compare-card__name"></div>
            <div class="color-compare-card__code"></div>            
          </div>
          <button type="button" class="color-compare-card__remove" aria-label="Remove color from comparison"><span class="svg-wrapper" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg></span></button>
        </div>
        <div class="color-compare-card__subtitle"></div>
      </div>
    `;
  }

  card.dataset.colorId = String(color.colorId ?? '');
  card.setAttribute('draggable', 'true');

  const linkEl = card.querySelector('.color-compare-card__link');
  if (linkEl instanceof HTMLAnchorElement) {
    linkEl.href = color.url || '#';
    linkEl.setAttribute('aria-label', color.colorName ? `View ${color.colorName}` : 'View color');
  }

  const swatch = card.querySelector('.color-compare-card__swatch-color');
  if (swatch instanceof HTMLElement) {
    swatch.style.backgroundColor = color.hexcode || '#CCCCCC';
  }

  const codeEl = card.querySelector('.color-compare-card__code');
  if (codeEl instanceof HTMLElement) {
    codeEl.textContent = (color.colorcollection && color.colorcollection.length)
      ? color.colorcollection.join(', ')
      : '';
  }

  const nameEl = card.querySelector('.color-compare-card__name');
  if (nameEl instanceof HTMLElement) {
    nameEl.textContent = color.colorName || '';
  }

  const subtitleEl = card.querySelector('.color-compare-card__subtitle');
  if (subtitleEl instanceof HTMLElement) {
    subtitleEl.textContent = color.description || '';
  }

  const wishlistHost = card.querySelector('wishlist-button');
  const wishlistBtn = wishlistHost?.querySelector('.color-card__heart-button');

  if (wishlistBtn instanceof HTMLButtonElement) {
    wishlistBtn.dataset.colorcode = color.hexcode || '';
    wishlistBtn.dataset.colorid = String(color.colorId ?? '');
    wishlistBtn.dataset.colorurl = color.url || '';
    wishlistBtn.dataset.colorname = color.colorName || '';
    wishlistBtn.dataset.colorref = color.colorRef || '';
    wishlistBtn.dataset.colorbase = color.colorBase || '';
    wishlistBtn.dataset.colordescription = color.description || '';
    wishlistBtn.dataset.colorcollection = (color.colorcollection || []).join(', ');
    wishlistBtn.dataset.colorfilter = (color.colorfilter || []).join(', ');
    wishlistBtn.dataset.coloratmosphere = (color.coloratmosphere || []).join(', ');
  }

  return card;
}

/**
 * Sets up drag-and-drop and remove handlers for all cards inside a container.
 * @param {HTMLElement} container
 */
function attachCardInteractions(container) {
  const cards = container.querySelectorAll('.color-compare-card');
  if (!cards.length) {
    return;
  }

  let dragIndex = -1;

  /**
   * Wire up drag-and-drop and remove behaviour for a single card:
   * - dragstart/dragover/drop: reorder columns in the compare list.
   * - dragend: clear visual state.
   * - click on .color-compare-card__remove: remove that color from the list.
   * Changes are persisted back into localStorage via setStoredList.
   * @param {HTMLElement} card
   * @param {number} index
   */
  const setupCard = (card, index) => {
    card.dataset.index = String(index);

    card.addEventListener('dragstart', (event) => {
      dragIndex = index;
      card.classList.add('is-dragging');

      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));

        const w = card.offsetWidth;
        const h = card.offsetHeight;
        const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
        clone.classList.add('color-compare-card--drag-image');
        clone.style.cssText = [
          'position:fixed',
          'left:-9999px',
          'top:0',
          `width:${w}px`,
          `height:${h}px`,
          'min-width:0',
          'max-width:none',
          'pointer-events:none',
          'z-index:9999',
        ].join(';');
        document.body.appendChild(clone);
        const offsetX = typeof event.offsetX === 'number' ? event.offsetX : w / 2;
        const offsetY = typeof event.offsetY === 'number' ? event.offsetY : h / 2;
        event.dataTransfer.setDragImage(clone, offsetX, offsetY);
        setTimeout(() => {
          clone.remove();
        }, 0);
      }
    });

    card.addEventListener('dragover', (event) => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
      card.classList.add('is-drag-over');
    });

    card.addEventListener('dragleave', () => {
      card.classList.remove('is-drag-over');
    });

    card.addEventListener('drop', (event) => {
      event.preventDefault();
      card.classList.remove('is-drag-over');

      const from = dragIndex;
      const to = index;

      if (from === -1 || from === to) {
        return;
      }

      const list = getStoredList();
      if (!list.length || from >= list.length || to >= list.length) {
        return;
      }

      const updated = [...list];
      const [moved] = updated.splice(from, 1);
      if (!moved) {
        return;
      }
      updated.splice(to, 0, moved);
      setStoredList(updated);
    });

    card.addEventListener('dragend', () => {
      card.classList.remove('is-dragging');
      dragIndex = -1;
    });

    const removeBtn = card.querySelector('.color-compare-card__remove');
    if (removeBtn instanceof HTMLButtonElement) {
      removeBtn.addEventListener('click', () => {
        const colorId = card.dataset.colorId;
        if (!colorId) {
          return;
        }

        const list = getStoredList();
        const updated = list.filter(
          (item) => String(/** @type {{ colorId?: string }} */ (item).colorId) !== String(colorId),
        );
        setStoredList(updated);
      });
    }
  };

  let index = 0;
  for (const cardNode of cards) {
    if (cardNode instanceof HTMLElement) {
      setupCard(cardNode, index);
      index += 1;
    }
  }
}

/**
 * Checks if a color (by colorId) is in the compare list.
 * @param {{ colorId?: string }[]} list - Compare list
 * @param {string | null} colorId - colorId to look for
 * @returns {boolean}
 */
function isInList(list, colorId) {
  if (colorId == null) return false;
  return list.some((item) => String(item.colorId) === String(colorId));
}

/**
 * Updates disabled state of all comparator buttons on the page.
 * When list has MAX_ITEMS, buttons not in the list get disabled and DISABLED_CLASS.
 * @param {object[]} list - Current compare list
 */
function updateAllButtonsState(list) {
  const isFull = list.length >= MAX_ITEMS;
  const allButtons = document.querySelectorAll('comparator-button .comparator-button');

  allButtons.forEach((el) => {
    const btn = /** @type {HTMLButtonElement} */ (el);
    const colorId = btn.getAttribute('data-colorid');
    const inList = isInList(list, colorId);

    if (isFull && !inList) {
      btn.classList.add(DISABLED_CLASS);
      btn.disabled = true;
    } else {
      btn.classList.remove(DISABLED_CLASS);
      btn.disabled = false;
    }
  });
}

/**
 * Syncs fill class on all comparator buttons from the current stored list.
 * @param {object[]} list - Current compare list
 */
function syncAllButtonsFillState(list) {
  const allButtons = document.querySelectorAll('comparator-button .comparator-button');
  allButtons.forEach((el) => {
    const btn = /** @type {HTMLButtonElement} */ (el);
    const colorId = btn.getAttribute('data-colorid');
    if (colorId) {
      if (isInList(list, colorId)) {
        btn.classList.add(FILL_CLASS);
      } else {
        btn.classList.remove(FILL_CLASS);
      }
    }
  });
}

/**
 * Custom element for the color compare button.
 *
 * Note: Click behaviour for the compare icon lives in a single place –
 * the document-level click listener at the bottom of this file. This keeps
 * add/remove logic consistent even when the icon is wrapped by other elements
 * (links, overlays, etc.).
 *
 * The element itself is responsible only for syncing initial visual state
 * (filled / disabled) when new buttons appear in the DOM, e.g. after
 * section rendering or infinite scroll.
 */
class ComparatorButton extends HTMLElement {
  connectedCallback() {
    // Defer so other comparator buttons in the same batch are in the DOM
    requestAnimationFrame(() => {
      const list = getStoredList();
      syncAllButtonsFillState(list);
      updateAllButtonsState(list);
    });
  }
}

customElements.define('comparator-button', ComparatorButton);

/**
 * Restore fill and disabled state from localStorage so .fill-comparator
 * persists after page refresh.
 */
function initComparatorState() {
  const list = getStoredList();
  syncAllButtonsFillState(list);
  updateAllButtonsState(list);
}

/**
 * Updates the floating compare button: visibility (show only when count > 0)
 * and text "Compare colors (N)" using label from data and count from list.
 * The underlying HTML is rendered by `snippets/color-compare-floating-button.liquid`
 * and included globally in layout/theme.liquid.
 */
function updateFloatingCompareButton() {
  const el = document.querySelector('color-floating-compare-button');
  if (!el) return;

  const list = getStoredList();
  const count = list.length;
  const countEl = el.querySelector('[data-compare-count]');
  const linkEl = el.querySelector('.color-floating-compare-button__link');
  const label = el.getAttribute('data-label') || 'Compare colors';

  if (countEl) {
    countEl.textContent = count > 0 ? `(${count})` : '(0)';
  }

  if (linkEl) {
    linkEl.setAttribute('aria-label', count > 0 ? `${label} (${count})` : label);
  }

  if (count > 0) {
    el.classList.add('is-visible');
    el.removeAttribute('aria-hidden');
  } else {
    el.classList.remove('is-visible');
    el.setAttribute('aria-hidden', 'true');
  }
}

/**
 * Handles updates to the compare list: floating compare button, cards UI, search bar buttons.
 * @param {CustomEvent & { detail?: { list?: CompareColor[] } }} event
 */
function handleCompareListUpdated(event) {
  const detail = event.detail;
  const list = detail && Array.isArray(detail.list) ? detail.list : getStoredList();
  updateFloatingCompareButton();
  renderCompareCardsIntoAllLists(list);
  updateSearchBarButtons(list);
  syncAllButtonsFillState(list);
  updateAllButtonsState(list);
}

/**
 * Enable/disable Add (when list has 5) and Reset (when list is empty).
 * @param {CompareColor[]} list
 */
function updateSearchBarButtons(list) {
  const addBtn = document.querySelector('[data-comparison-add]');
  const resetBtn = document.querySelector('[data-comparison-reset]');
  if (addBtn instanceof HTMLButtonElement) {
    addBtn.disabled = list.length >= MAX_ITEMS;
  }
  if (resetBtn instanceof HTMLButtonElement) {
    resetBtn.disabled = list.length === 0;
  }
}

/**
 * Add a color to the compare list from a dropdown option (same data-* as comparator button).
 * @param {HTMLButtonElement} optionButton
 * @returns {boolean} true if added
 */
function addColorFromOption(optionButton) {
  const colorId = optionButton.getAttribute('data-colorid');
  if (!colorId) return false;

  const list = getStoredList();
  if (isInList(list, colorId)) return false;
  if (list.length >= MAX_ITEMS) {
    if (typeof alert !== 'undefined') alert(WARNING_MESSAGE);
    return false;
  }

  const colorObj = getColorObjectFromButton(optionButton);
  setStoredList([...list, colorObj]);
  return true;
}

/**
 * Filter dropdown by current input: only options whose code or name contain the full query string (every letter, in order) are shown.
 * Empty input = show all. No matches = hide dropdown. Uses display so theme CSS cannot override.
 * Called by initSearchBar whenever the search input changes.
 * @param {HTMLInputElement} input
 * @param {HTMLElement} dropdown
 */
function filterDropdownByInput(input, dropdown) {
  const query = (input.value || '').trim().toLowerCase();
  const options = dropdown.querySelectorAll('.color-comparison-search-cards__dropdown-item');
  let visibleCount = 0;

  for (const option of options) {
    if (!(option instanceof HTMLButtonElement)) continue;
    const label = option.querySelector('.color-comparison-search-cards__dropdown-label')?.textContent?.trim().toLowerCase() ?? '';
    const ref = (option.getAttribute('data-colorref') ?? '').toLowerCase();
    const name = (option.getAttribute('data-colorname') ?? '').toLowerCase();
    const searchable = [ref, name, label].filter(Boolean).join(' ');
    const matches = query === '' || searchable.includes(query);
    option.style.display = matches ? '' : 'none';
    option.hidden = !matches;
    if (matches) visibleCount += 1;
  }

  dropdown.hidden = visibleCount === 0;
}

/**
 * Wire up the search bar inside the comparator block:
 * - Filters the dropdown list of colors as the user types.
 * - On dropdown click: copies the label into the input (does not add yet).
 * - On "Add" click: finds the first matching dropdown option and adds it to
 *   the compare list (via addColorFromOption).
 * - On "Reset" click: clears the entire compare list.
 */
function initSearchBar() {
  const input = document.querySelector('[data-comparison-search-input]');
  const dropdownEl = document.querySelector('[data-comparison-dropdown]');
  const addBtn = document.querySelector('[data-comparison-add]');
  const resetBtn = document.querySelector('[data-comparison-reset]');

  if (!(input instanceof HTMLInputElement) || !(dropdownEl instanceof HTMLElement)) return;

  const dropdown = /** @type {HTMLElement} */ (dropdownEl);

  function showDropdown() {
    filterDropdownByInput(
      /** @type {HTMLInputElement} */ (input),
      /** @type {HTMLElement} */ (dropdown),
    );
  }

  function hideDropdown() {
    dropdown.hidden = true;
  }

  input.addEventListener('focus', showDropdown);
  input.addEventListener('input', showDropdown);
  input.addEventListener('keyup', showDropdown);

  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (target && !input.closest('.color-comparison-search-cards__input-wrap')?.contains(target)) {
      hideDropdown();
    }
  });

  dropdown.addEventListener('click', (e) => {
    const option = (e.target instanceof Element ? e.target : null)?.closest('.color-comparison-search-cards__dropdown-item');
    if (!(option instanceof HTMLButtonElement)) return;
    e.preventDefault();
    const added = addColorFromOption(option);

    // UX: click option = add immediately. If it can't be added (already present),
    // keep the input in sync so the user understands what they clicked.
    const label = option.querySelector('.color-comparison-search-cards__dropdown-label')?.textContent?.trim() ?? '';
    input.value = added ? '' : label;
    hideDropdown();
    input.focus();
  });

  if (addBtn) {
    addBtn.addEventListener('click', () => {
      const query = input.value.trim().toLowerCase();
      if (!query) return;

      const options = dropdown.querySelectorAll('.color-comparison-search-cards__dropdown-item');
      for (const opt of options) {
        if (!(opt instanceof HTMLButtonElement)) continue;
        const label = opt.querySelector('.color-comparison-search-cards__dropdown-label')?.textContent?.trim().toLowerCase() ?? '';
        if (label.includes(query)) {
          if (addColorFromOption(opt)) {
            input.value = '';
            hideDropdown();
          }
          return;
        }
      }
    });
  }

  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      setStoredList([]);
    });
  }

  updateSearchBarButtons(getStoredList());
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initComparatorState();
    updateFloatingCompareButton();
    renderCompareCardsIntoAllLists(getStoredList());
    initSearchBar();
  });
} else {
  initComparatorState();
  updateFloatingCompareButton();
  renderCompareCardsIntoAllLists(getStoredList());
  initSearchBar();
}

window.addEventListener('color-compare-list-updated', handleCompareListUpdated);

/**
 * Document-level click delegation so comparator buttons work even when
 * the overlay link or dynamic content would otherwise capture the click.
 */
document.addEventListener('click', (event) => {
  const targetEl = event.target instanceof Element ? event.target : null;
  const host = targetEl?.closest('comparator-button') ?? null;
  if (!host) return;

  const btn = host.querySelector('.comparator-button');
  if (!btn || !(btn instanceof HTMLButtonElement) || (targetEl !== btn && !btn.contains(targetEl))) return;

  event.preventDefault();
  event.stopPropagation();

  if (btn.disabled) return;

  const colorId = btn.getAttribute('data-colorid');
  if (!colorId) return;

  let list = getStoredList();
  const inList = isInList(list, colorId);

  if (inList) {
    list = list.filter((item) => String(/** @type {{ colorId?: string }} */ (item).colorId) !== String(colorId));
  } else {
    if (list.length >= MAX_ITEMS) {
      if (typeof alert !== 'undefined') alert(WARNING_MESSAGE);
      return;
    }
    const colorObj = getColorObjectFromButton(btn);
    list = [...list, colorObj];
  }

  setStoredList(list);
}, true);
